import { useCallback, useEffect, useMemo, useState, type RefObject } from "react";

/**
 * Window a long list inside its own scroll container.
 *
 * Only the items in view render. Heights are supplied per item so group headers
 * and rows can differ: prefix sums give each item's offset, and a binary search
 * finds the first visible one, so a list of thousands costs the same as a list
 * of ten.
 */
export function useVirtualList(
  heights: number[],
  ref: RefObject<HTMLDivElement | null>,
  overscan = 6,
) {
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(600);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setViewport(el.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  const offsets = useMemo(() => {
    const out = new Array<number>(heights.length);
    let acc = 0;
    for (let i = 0; i < heights.length; i += 1) {
      out[i] = acc;
      acc += heights[i];
    }
    return out;
  }, [heights]);

  const totalHeight = useMemo(
    () => (heights.length ? offsets[heights.length - 1] + heights[heights.length - 1] : 0),
    [offsets, heights],
  );

  const indexAt = useCallback(
    (y: number) => {
      let lo = 0;
      let hi = offsets.length - 1;
      let answer = 0;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (offsets[mid] <= y) {
          answer = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      return answer;
    },
    [offsets],
  );

  const start = heights.length ? Math.max(0, indexAt(scrollTop) - overscan) : 0;
  const end = heights.length
    ? Math.min(heights.length, indexAt(scrollTop + viewport) + 1 + overscan)
    : 0;
  const topPad = heights.length ? offsets[start] : 0;
  const bottomPad = heights.length
    ? totalHeight - offsets[end - 1] - heights[end - 1]
    : 0;

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => setScrollTop(e.currentTarget.scrollTop),
    [],
  );

  return { onScroll, start, end, topPad, bottomPad, totalHeight };
}
